/**
 * The card drawer.
 *
 * Everything about a single card, in one panel: the fields, the description, the
 * comment thread, the attachments and the card's own event history.
 *
 * ## Why it is a side panel and not a modal
 *
 * The board stays visible and interactive behind it. A card is rarely edited in
 * isolation - the reason you opened it is usually something you can see on the
 * board - and a modal that covers the columns forces a close-and-reopen cycle for
 * every comparison. It also means a peer's drag is still visible while you are
 * reading, which is the whole point of a collaborative board.
 *
 * ## Why the title is debounced but the priority is not
 *
 * A text field fires a change per keystroke. The optimistic overlay updates on
 * every one of them - the field is uncontrolled and immediate - but the *send* is
 * debounced by 500 ms, so a fast typist produces one frame per pause rather than
 * one per character. `BoardProvider.updateCard` then coalesces further: edits that
 * arrive while a frame is in flight merge into the pending operation and are sent
 * on the next round trip. A select or a date input fires once, so debouncing it
 * would only add latency.
 *
 * ## Why the history panel is not live
 *
 * It is fetched once with the rest of the card detail and not updated by the
 * socket. The board's activity feed is the live surface; a second live list inside
 * the drawer would mean two subscriptions for the same data, and the drawer's
 * history is a per-card view that is read once and closed. The panel says so
 * rather than silently going stale.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Card, Priority, PublicUser } from '@slate/shared';
import { PRIORITIES } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';
import { Avatar } from './Avatar.js';
import { CommentThread } from './CommentThread.js';
import { AttachmentPanel } from './AttachmentPanel.js';
import { describeEvent, type DescribeContext } from './ActivityFeed.js';
import {
  Icon,
  ghostButtonClass,
  inputClass,
  useDebounced,
  useTick,
} from './ui.js';
import { PRIORITY_LABEL, absoluteTime, relativeTime } from '../lib/format.js';
import { renderMarkdown } from '../lib/markdown.js';

export function CardDrawer({
  card,
  members,
  currentUserId,
  onClose,
}: {
  card: Card;
  members: Map<string, PublicUser>;
  currentUserId: string;
  onClose(): void;
}): ReactNode {
  const { state, actions } = useBoard();
  const [tab, setTab] = useState<'comments' | 'files' | 'history'>('comments');
  const [editingDescription, setEditingDescription] = useState(false);

  // Fetch the detail payload once per card. `openCardDetail` is idempotent and the
  // reducer replaces the three maps wholesale, so a second call for the same card
  // is harmless - which matters under StrictMode's double-invoked effects.
  useEffect(() => {
    void actions.openCardDetail(card.id);
  }, [card.id, actions]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const assignee = card.assigneeId === null ? null : (members.get(card.assigneeId) ?? null);
  const column = state.columns.find((entry) => entry.id === card.columnId);

  const now = useTick(true, 30_000);
  const describeContext = useMemo<DescribeContext>(
    () => ({
      members,
      columnNames: new Map(state.columns.map((entry) => [entry.id, entry.name])),
    }),
    [members, state.columns],
  );

  const history = state.history[card.id];
  const commentCount = state.comments[card.id]?.filter((entry) => entry.deletedAt === null).length ?? null;
  const attachmentCount = state.attachments[card.id]?.length ?? null;

  return (
    <aside
      aria-label={`Card: ${card.title}`}
      className="flex w-[26rem] shrink-0 flex-col border-l border-border bg-surface"
    >
      <header className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <TitleField cardId={card.id} title={card.title} />
          <p className="mt-1 text-[11px] text-ink-subtle">
            in {column?.name ?? 'a deleted column'}
            <span className="mx-1">·</span>
            <span className="font-mono">v{card.version}</span>
            <span className="mx-1">·</span>
            edited {relativeTime(card.updatedAt, now)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close card"
          className="rounded p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
        >
          <Icon name="close" />
        </button>
      </header>

      <div className="scroll-slim min-h-0 flex-1 overflow-y-auto">
        <dl className="grid grid-cols-[7rem_1fr] items-center gap-x-3 gap-y-2 px-4 py-3">
          <dt className="text-xs text-ink-muted">Priority</dt>
          <dd>
            <label className="sr-only" htmlFor={`priority-${card.id}`}>
              Priority
            </label>
            <select
              id={`priority-${card.id}`}
              value={card.priority}
              onChange={(event) => actions.updateCard(card.id, { priority: event.target.value as Priority })}
              className={inputClass}
            >
              {PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {PRIORITY_LABEL[priority]}
                </option>
              ))}
            </select>
          </dd>

          <dt className="text-xs text-ink-muted">Assignee</dt>
          <dd className="flex items-center gap-2">
            {assignee !== null && <Avatar name={assignee.name} colour={assignee.colour} size={20} title={assignee.name} />}
            <label className="sr-only" htmlFor={`assignee-${card.id}`}>
              Assignee
            </label>
            <select
              id={`assignee-${card.id}`}
              value={card.assigneeId ?? ''}
              onChange={(event) => actions.updateCard(card.id, { assigneeId: event.target.value === '' ? null : event.target.value })}
              className={inputClass}
            >
              <option value="">Unassigned</option>
              {[...members.values()].map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </dd>

          <dt className="text-xs text-ink-muted">Due</dt>
          <dd>
            <label className="sr-only" htmlFor={`due-${card.id}`}>
              Due date
            </label>
            <input
              id={`due-${card.id}`}
              type="date"
              value={card.dueDate ?? ''}
              onChange={(event) => actions.updateCard(card.id, { dueDate: event.target.value === '' ? null : event.target.value })}
              className={inputClass}
            />
          </dd>

          <dt className="text-xs text-ink-muted">Labels</dt>
          <dd>
            {state.labels.length === 0 ? (
              <p className="text-xs text-ink-subtle">
                No labels on this board. Labels are seeded, not created from the UI - see docs/DATA-MODEL.md.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {state.labels.map((label) => {
                  const active = card.labels.some((entry) => entry.id === label.id);
                  return (
                    <button
                      key={label.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        const ids = card.labels.map((entry) => entry.id);
                        const next = active ? ids.filter((id) => id !== label.id) : [...ids, label.id];
                        actions.updateCard(card.id, { labelIds: next });
                      }}
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                        active ? 'border-transparent' : 'border-border text-ink-muted hover:text-ink'
                      }`}
                      style={active ? { backgroundColor: `${label.colour}22`, color: label.colour } : undefined}
                    >
                      {label.name}
                    </button>
                  );
                })}
              </div>
            )}
          </dd>
        </dl>

        <section aria-label="Description" className="border-t border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold tracking-wide text-ink-muted uppercase">Description</h3>
            <button
              type="button"
              onClick={() => setEditingDescription((editing) => !editing)}
              className="rounded px-1.5 py-0.5 text-[11px] text-ink-subtle hover:bg-sunken hover:text-ink"
            >
              {editingDescription ? 'Preview' : 'Edit'}
            </button>
          </div>

          {editingDescription ? (
            <DescriptionEditor
              key={card.id}
              cardId={card.id}
              initial={card.description ?? ''}
              onDone={() => setEditingDescription(false)}
            />
          ) : card.description === null || card.description.trim() === '' ? (
            <p className="mt-2 text-sm text-ink-subtle">No description. Use Edit to add one.</p>
          ) : (
            <div className="mt-2 text-sm text-ink">{renderMarkdown(card.description, `d-${card.id}`)}</div>
          )}
        </section>

        <div className="border-t border-border">
          <div role="tablist" aria-label="Card detail sections" className="flex gap-1 px-3 pt-2">
            <TabButton active={tab === 'comments'} onClick={() => setTab('comments')} count={commentCount}>
              Comments
            </TabButton>
            <TabButton active={tab === 'files'} onClick={() => setTab('files')} count={attachmentCount}>
              Files
            </TabButton>
            <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
              History
            </TabButton>
          </div>

          <div className="px-4 py-3">
            {tab === 'comments' && <CommentThread cardId={card.id} currentUserId={currentUserId} />}

            {tab === 'files' && <AttachmentPanel cardId={card.id} />}

            {tab === 'history' && (
              <div>
                {history === undefined ? (
                  <p className="text-xs text-ink-subtle">Loading history…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-ink-subtle">Nothing has happened to this card yet.</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {history.map((event) => {
                      const described = describeEvent(event, describeContext);
                      return (
                        <li key={event.id} className="flex gap-2 text-xs">
                          <Avatar name={described.actorName} colour={described.actorColour} size={18} />
                          <span className="min-w-0 flex-1 text-ink-muted">
                            <span className="font-medium text-ink">{described.actorName}</span> {described.text}
                            <span className="mt-0.5 block text-[11px] text-ink-subtle" title={absoluteTime(event.createdAt)}>
                              {relativeTime(event.createdAt, now)}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                )}
                <p className="mt-3 text-[11px] text-ink-subtle">
                  This list is a snapshot from when the card was opened. The board&apos;s activity panel is the live view.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="flex items-center gap-2 border-t border-border px-4 py-2.5">
        <button
          type="button"
          className={ghostButtonClass}
          onClick={() => {
            actions.archiveCard(card.id, true);
            onClose();
          }}
        >
          <Icon name="archive" size={14} />
          Archive
        </button>
        <button
          type="button"
          className={`${ghostButtonClass} ml-auto text-danger hover:bg-danger-soft`}
          onClick={() => {
            void actions.deleteCard(card.id);
            onClose();
          }}
        >
          <Icon name="trash" size={14} />
          Delete
        </button>
      </footer>
    </aside>
  );
}

/**
 * The title field.
 *
 * Uncontrolled while focused and controlled from `useDebounced` otherwise. That
 * combination is what makes it feel immediate without sending a frame per
 * keystroke: the local `useState` drives the input, and the debounced copy is what
 * triggers the mutation.
 *
 * The one thing this must not do is reset the input from props on every render. A
 * controlled input whose value comes from the optimistic overlay would be
 * rewritten by the acknowledgement a moment later, and any characters typed in
 * between would be lost - the classic controlled-input race in an optimistic UI.
 */
function TitleField({ cardId, title }: { cardId: string; title: string }): ReactNode {
  const { actions } = useBoard();
  const [value, setValue] = useState(title);
  const settled = useDebounced(value, 500);

  // Adopt a title that changed underneath us (a peer's edit, or a rollback) only
  // when the field is not the source of the change. Comparing against the last
  // value we sent is how that is detected without tracking focus.
  const [lastSent, setLastSent] = useState(title);
  useEffect(() => {
    if (title !== lastSent && settled === lastSent) {
      setValue(title);
      setLastSent(title);
    }
  }, [title, lastSent, settled]);

  useEffect(() => {
    const trimmed = settled.trim();
    if (trimmed === '' || trimmed === lastSent) return;
    setLastSent(trimmed);
    actions.updateCard(cardId, { title: trimmed });
  }, [settled, lastSent, cardId, actions]);

  return (
    <>
      <label className="sr-only" htmlFor={`title-${cardId}`}>
        Card title
      </label>
      <input
        id={`title-${cardId}`}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        className="w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-base font-semibold text-ink hover:border-border focus:border-accent"
      />
    </>
  );
}

/** The description editor.
 *
 * A plain textarea, saved on blur. There is no rich-text toolbar and no live
 * preview side by side - the Preview toggle is one click and a split pane in a
 * 26 rem drawer would leave neither half readable. */
function DescriptionEditor({
  cardId,
  initial,
  onDone,
}: {
  cardId: string;
  initial: string;
  onDone(): void;
}): ReactNode {
  const { actions } = useBoard();
  const [value, setValue] = useState(initial);

  return (
    <div className="mt-2">
      <label className="sr-only" htmlFor={`description-${cardId}`}>
        Card description, Markdown supported
      </label>
      <textarea
        id={`description-${cardId}`}
        value={value}
        rows={10}
        autoFocus
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => {
          const next = value.trim();
          if (next !== initial.trim()) actions.updateCard(cardId, { description: next === '' ? null : next });
          onDone();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onDone();
        }}
        className="scroll-slim w-full resize-y rounded-md border border-border bg-canvas p-2 font-mono text-xs leading-relaxed text-ink"
      />
      <p className="mt-1 text-[11px] text-ink-subtle">
        Markdown: **bold**, *italic*, `code`, - lists, # headings, [links](https://example.com), @mentions.
      </p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick(): void;
  count?: number | null;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-sunken text-ink' : 'text-ink-muted hover:text-ink'
      }`}
    >
      {children}
      {count !== undefined && count !== null && <span className="ml-1.5 text-ink-subtle tabular-nums">{count}</span>}
    </button>
  );
}
